import axios from 'axios'

const DEFAULT_API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? ''
const TIMESHEET_API_BASE_URL = (process.env.REACT_APP_TIMESHEET_API_BASE_URL ?? DEFAULT_API_BASE_URL).replace(/\/$/, '')

const buildUrl = (path: string) => {
  if (!TIMESHEET_API_BASE_URL) {
    return path
  }
  return `${TIMESHEET_API_BASE_URL}${path}`
}

type LegacyApiResponse<T> = {
  isSuccess?: boolean
  message?: string | null
  data?: T | null
}

type LegacyPagedData<T> = {
  items?: T[] | null
  currentPage?: number | null
  pageSize?: number | null
  totalPage?: number | null
  totalRecord?: number | null
}

export interface TimesheetSearchParams {
  permitNo?: string | null
  stDateTime?: string | null
  expDateTime?: string | null
  pageIndex?: number
  pageSize?: number
}

export interface TimesheetItem {
  auditId?: string | null
  permitId: string
  permitNo: string
  company?: string | null
  companyName?: string | null
  plant?: string | null
  unit?: string | null
  workDescr?: string | null
  stDateTime?: string | null
  expDateTime?: string | null
  permitStatusD?: string | null
  time1Value?: string | null
  time2Value?: string | null
  time3Value?: string | null
  time4Value?: string | null
  time5Value?: string | null
}

export interface TimesheetRecord {
  permitId: string
  remark?: string | null
  time1Value?: string | null
  time1AddDate?: string | null
  time1AddByUserId?: string | null
  time1AddByName?: string | null
  time2Value?: string | null
  time2AddDate?: string | null
  time2AddByUserId?: string | null
  time2AddByName?: string | null
  time3Value?: string | null
  time3AddDate?: string | null
  time3AddByUserId?: string | null
  time3AddByName?: string | null
  time4Value?: string | null
  time4AddDate?: string | null
  time4AddByUserId?: string | null
  time4AddByName?: string | null
  time5Value?: string | null
  time5AddDate?: string | null
  time5AddByUserId?: string | null
  time5AddByName?: string | null
}

export interface TimesheetPagedResult {
  items: TimesheetItem[]
  currentPage: number
  pageSize: number
  totalPage: number
  totalRecord: number
}

export interface SaveTimesheetRequest extends TimesheetRecord {}

const ensureSuccess = <T>(response: LegacyApiResponse<T>, fallbackMessage: string) => {
  if (response?.isSuccess === false) {
    throw new Error(response.message ?? fallbackMessage)
  }
}

export async function searchTimesheets(params: TimesheetSearchParams): Promise<TimesheetPagedResult> {
  const response = await axios.post<LegacyApiResponse<LegacyPagedData<TimesheetItem>>>(
    buildUrl('/api/v1/Timesheet'),
    params,
  )

  ensureSuccess(response.data, 'Unable to load timesheet list.')

  const data = response.data?.data
  return {
    items: data?.items ?? [],
    currentPage: data?.currentPage ?? params.pageIndex ?? 1,
    pageSize: data?.pageSize ?? params.pageSize ?? 10,
    totalPage: data?.totalPage ?? 1,
    totalRecord: data?.totalRecord ?? 0,
  }
}

export async function getTimesheetByPermitId(permitId: string): Promise<TimesheetRecord | null> {
  const response = await axios.get<LegacyApiResponse<TimesheetRecord>>(buildUrl(`/api/v1/Timesheet/${permitId}`))

  if (response.data?.isSuccess === false) {
    return null
  }

  return response.data?.data ?? null
}

export async function saveTimesheet(payload: SaveTimesheetRequest): Promise<void> {
  const response = await axios.post<LegacyApiResponse<string>>(buildUrl('/api/v1/Timesheet/save'), payload)
  ensureSuccess(response.data, 'Unable to save timesheet.')
}

export async function deleteTimesheet(permitId: string): Promise<void> {
  const response = await axios.post<LegacyApiResponse<string>>(buildUrl('/api/v1/Timesheet/delete'), {
    permitId,
  })
  ensureSuccess(response.data, 'Unable to delete timesheet.')
}
